import { DOCUMENT } from '@angular/common'
import type { OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core'
import { Directive, ElementRef, NgZone, Renderer2, inject, input, output } from '@angular/core'
import { HapticService } from '../../services/haptic-service/haptic.service'
import { RowSwipeService } from './row-swipe.service'

/** Movement in px before the gesture locks to a horizontal drag or a vertical scroll. */
const LOCK_SLOP = 8
/** A stationary hold opens the row's alternate action surface. */
const LONG_PRESS_MS = 500
/** Horizontal travel in px before the action tray can appear. */
const ACTION_REVEAL_THRESHOLD = 24
/** Time window in ms for the release velocity. */
const VELOCITY_WINDOW = 100
/** UIScrollView normal deceleration rate per ms; velocity × this factor gives the projected rest point. */
const PROJECTION_FACTOR = 0.998 / (1 - 0.998)
/** Rubber-band constant used by UIScrollView. */
const RUBBER_BAND = 0.55
/** Spring settle: critically damped, about 0.35 s to rest. */
const SPRING_STIFFNESS = 320
const SPRING_DAMPING = 2 * Math.sqrt(SPRING_STIFFNESS)
const SPRING_MAX_MS = 700
const SPRING_STEP_MS = 1000 / 60

type GestureState = 'idle' | 'pending' | 'drag' | 'scroll' | 'hold'

/**
 * Swipes a row sideways to reveal its actions, like a native iOS list row.
 * The row follows the finger, resists past the action width, and settles with a spring.
 */
@Directive({
  selector: '[awRowSwipe]',
  host: {
    '(touchstart)': 'touchStart($event)',
    '(touchmove)': 'touchMove($event)',
    '(touchend)': 'touchEnd($event)',
    '(touchcancel)': 'touchCancel()',
    '(mousedown)': 'mouseStart($event)',
    '(contextmenu)': 'onContextMenu($event)'
  }
})
export class RowSwipeDirective implements OnInit, OnChanges, OnDestroy {
  private renderer = inject(Renderer2)
  private rowSwipeService = inject(RowSwipeService)
  private hapticService = inject(HapticService)
  private row = inject<ElementRef<HTMLElement>>(ElementRef)
  private ngZone = inject(NgZone)
  private document = inject(DOCUMENT)

  readonly dragLimit = input(138)
  readonly swipeDisabled = input(false)
  readonly longPressEnabled = input(false)
  readonly rowActions = input<HTMLElement>()
  readonly slideActions = input(false)
  readonly swipeAction = input(false)
  readonly swipeActionOffset = input(20)
  readonly swipeDirection = input<'left' | 'right'>('left')

  readonly showActions = output<boolean>()
  readonly onSwipeAction = output<void>()
  readonly longPressed = output<void>()

  private state: GestureState = 'idle'
  private isOpen = false
  /** Current row translation in px. */
  private position = 0
  /** Row translation when the gesture started. */
  private basePosition = 0
  private startX = 0
  private startY = 0
  private samples: { x: number; t: number }[] = []
  private hasRevealedActions = false
  private fullSwipeArmed = false
  private suppressNextClick = false
  /** Mouse events that arrive before this time follow a touch and are ignored. */
  private ignoreMouseUntil = 0
  private removeListeners: (() => void)[] = []
  private removeOutsideListener?: () => void
  private removeClickListener?: () => void
  private removeTrayListeners: (() => void)[] = []
  private gestureOnActions = false
  private settleFrame?: number
  private settleTimer?: ReturnType<typeof setTimeout>
  private clickResetTimer?: ReturnType<typeof setTimeout>
  private holdTimer?: ReturnType<typeof setTimeout>

  /** Hides the action tray and blocks clicks that end a drag. */
  ngOnInit(): void {
    this.renderTray(0)
    this.setTrayVisible(false)
    this.renderer.setStyle(this.row.nativeElement, 'touch-action', 'pan-y')

    const onClick = (event: MouseEvent): void => {
      if (!this.suppressNextClick) {
        return
      }

      event.preventDefault()
      event.stopImmediatePropagation()
      this.suppressNextClick = false
    }

    this.row.nativeElement.addEventListener('click', onClick, { capture: true })
    this.removeClickListener = () => this.row.nativeElement.removeEventListener('click', onClick, { capture: true })

    const tray = this.rowActions()

    if (tray) {
      const listeners: [string, EventListener][] = [
        ['touchstart', event => this.touchStart(event as TouchEvent)],
        ['touchmove', event => this.touchMove(event as TouchEvent)],
        ['touchend', event => this.touchEnd(event as TouchEvent)],
        ['touchcancel', () => this.touchCancel()],
        ['mousedown', event => this.mouseStart(event as MouseEvent)],
        ['click', event => onClick(event as MouseEvent)]
      ]

      for (const [type, listener] of listeners) {
        tray.addEventListener(type, listener, { capture: true })
        this.removeTrayListeners.push(() => tray.removeEventListener(type, listener, { capture: true }))
      }
    }
  }

  /**
   * Closes the row when its actions become disabled or their width changes.
   * @param {SimpleChanges} changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['swipeDisabled']?.currentValue || changes['longPressEnabled']) {
      clearTimeout(this.holdTimer)
    }

    if ((changes['swipeDisabled']?.currentValue || changes['dragLimit']) && (this.isOpen || this.state === 'drag')) {
      this.closeRow()
    }
  }

  /** Removes listeners, timers, and running animations. */
  ngOnDestroy(): void {
    this.setGesturePressing(false)
    this.stopGestureListeners()
    this.stopSettle()
    this.removeOutsideListener?.()
    this.removeClickListener?.()
    this.removeTrayListeners.forEach(remove => remove())
    clearTimeout(this.clickResetTimer)
    clearTimeout(this.holdTimer)
    this.rowSwipeService.unregisterRow(this)

    if (this.state === 'drag') {
      this.rowSwipeService.inProgress = false
    }
  }

  /**
   * Leaves text selection and drag handles to their own controls.
   * @param {EventTarget | null} target Gesture start element.
   * @returns {boolean} Whether the control owns the gesture.
   */
  private isEditingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) {
      return false
    }

    const field = target.closest('input, textarea, [contenteditable="true"]')

    return !!target.closest('select, .cdk-drag-handle') || (!!field && field === this.document.activeElement)
  }

  /**
   * Checks whether a gesture starts on an exposed action.
   * @param {EventTarget | null} target Gesture target.
   * @returns {boolean} Whether the target is in the action tray.
   */
  private isActionTarget(target: EventTarget | null): boolean {
    return target instanceof Node && !!this.rowActions()?.contains(target)
  }

  /**
   * Starts watching a touch. Nothing moves until the finger travels sideways.
   * @param {TouchEvent} event
   */
  touchStart(event: TouchEvent): void {
    if (event.touches.length !== 1) {
      this.touchCancel()

      return
    }

    if (this.swipeDisabled() || this.isEditingTarget(event.target)) {
      return
    }

    this.gestureOnActions = this.isActionTarget(event.target)
    this.beginGesture(event.touches[0].clientX, event.touches[0].clientY)
  }

  /**
   * Follows the finger once the gesture is locked horizontal.
   * @param {TouchEvent} event
   */
  touchMove(event: TouchEvent): void {
    if (event.touches.length !== 1) {
      this.touchCancel()

      return
    }

    if (this.state === 'scroll' && this.gestureOnActions) {
      const dx = event.touches[0].clientX - this.startX
      const openingDirection = this.swipeDirection() === 'left' ? -1 : 1
      const closingAtStart = dx * openingDirection < 0
      const tray = this.rowActions()

      if (closingAtStart && (!tray || tray.scrollLeft <= 0)) {
        this.startDrag(dx)
        this.pointerMove(event.touches[0].clientX, event.touches[0].clientY)

        if (event.cancelable) {
          event.preventDefault()
        }
      }
    } else if (this.state === 'pending' || this.state === 'drag') {
      this.pointerMove(event.touches[0].clientX, event.touches[0].clientY)

      if (this.gestureOnActions && this.state === 'drag' && event.cancelable) {
        event.preventDefault()
      }
    }
  }

  /**
   * Releases the row and lets it settle.
   * @param {TouchEvent} event
   */
  touchEnd(event: TouchEvent): void {
    // Browsers replay a touch as mouse events; those must not start a second gesture.
    this.ignoreMouseUntil = Date.now() + 700
    this.release(event)
  }

  /** The browser took the touch for scrolling: settle to the nearer state without velocity. */
  touchCancel(): void {
    this.ignoreMouseUntil = Date.now() + 700
    clearTimeout(this.holdTimer)
    this.stopGestureListeners()

    if (this.state === 'drag') {
      this.samples = []
      this.release()
    } else {
      this.setGesturePressing(false)
      this.state = 'idle'
    }
  }

  /**
   * Starts a mouse drag for desktop browsers.
   * @param {MouseEvent} event
   */
  mouseStart(event: MouseEvent): void {
    if (
      this.swipeDisabled() ||
      event.button !== 0 ||
      Date.now() < this.ignoreMouseUntil ||
      this.isEditingTarget(event.target)
    ) {
      return
    }

    this.gestureOnActions = this.isActionTarget(event.target)
    this.beginGesture(event.clientX, event.clientY)
    this.ngZone.runOutsideAngular(() => {
      const onMove = (moveEvent: MouseEvent): void => {
        if (this.state === 'drag') {
          moveEvent.preventDefault()
        }

        this.pointerMove(moveEvent.clientX, moveEvent.clientY)
      }
      const onUp = (upEvent: MouseEvent): void => this.ngZone.run(() => this.release(upEvent))

      this.document.addEventListener('mousemove', onMove, { capture: true })
      this.document.addEventListener('mouseup', onUp, { capture: true })
      this.removeListeners.push(
        () => this.document.removeEventListener('mousemove', onMove, { capture: true }),
        () => this.document.removeEventListener('mouseup', onUp, { capture: true })
      )
    })
  }

  /**
   * Keeps native callouts from competing with the row actions.
   * @param {MouseEvent} event Native context menu.
   */
  onContextMenu(event: MouseEvent): void {
    if (this.longPressEnabled() && !this.swipeDisabled() && !this.isEditingTarget(event.target)) {
      event.preventDefault()
    }
  }

  /**
   * Opens the row so the whole action tray is visible.
   * @param {boolean} animate Whether to spring to the open position.
   */
  openRow(animate = true): void {
    this.renderer.removeClass(this.row.nativeElement, 'row-swipe-closing')
    this.isOpen = true
    this.rowSwipeService.registerRow(this)
    this.rowSwipeService.inProgress = false
    this.setTrayVisible(true)
    this.showActions.emit(true)
    this.listenForOutsideTap()

    if (!animate) {
      this.stopSettle()
      this.position = this.openPosition()
      this.renderRow(this.position)
      this.renderTray(this.position)

      return
    }

    this.settle(this.openPosition(), this.releaseVelocity())
  }

  /** Closes the row and hides the action tray once it is out of view. */
  closeRow(): void {
    clearTimeout(this.holdTimer)
    this.stopGestureListeners()
    this.setGesturePressing(false)
    this.state = 'idle'

    if (this.position !== 0) {
      this.renderer.addClass(this.row.nativeElement, 'row-swipe-closing')
    }

    this.rowSwipeService.unregisterRow(this)
    this.rowSwipeService.inProgress = false
    this.removeOutsideListener?.()
    this.removeOutsideListener = undefined
    this.settle(0, this.releaseVelocity(), () => {
      this.isOpen = false
      this.setTrayVisible(false)

      const rowActions = this.rowActions()

      if (rowActions) {
        this.renderer.removeClass(rowActions, 'swipe-row__actions--revealed')
      }

      this.showActions.emit(false)
    })
  }

  /**
   * Checks whether this row belongs to a surface.
   * @param {Node} root Surface element.
   * @returns {boolean} Whether the row is inside the surface.
   */
  isWithin(root: Node): boolean {
    return root.contains(this.row.nativeElement)
  }

  /**
   * Records where a gesture starts. Styles stay untouched until it locks horizontal.
   * @param {number} x
   * @param {number} y
   */
  private beginGesture(x: number, y: number): void {
    this.stopGestureListeners()
    this.stopSettle()
    this.renderer.removeClass(this.row.nativeElement, 'row-swipe-closing')
    this.state = 'pending'
    this.setGesturePressing(true)
    this.startX = x
    this.startY = y
    this.basePosition = this.position
    this.samples = [{ x, t: performance.now() }]
    // An already-open row is already past the reveal threshold. Keep that
    // state while the gesture moves back so a closing swipe can render and
    // settle the tray instead of resetting its position without a render.
    this.hasRevealedActions = this.isOpen || Math.abs(this.basePosition) > 0.3
    this.fullSwipeArmed = false
    this.suppressNextClick = false
    clearTimeout(this.clickResetTimer)
    clearTimeout(this.holdTimer)

    const onBlur = (): void => this.ngZone.run(() => this.touchCancel())

    this.document.defaultView?.addEventListener('blur', onBlur)
    this.removeListeners.push(() => this.document.defaultView?.removeEventListener('blur', onBlur))

    if (this.longPressEnabled() && !this.gestureOnActions) {
      this.holdTimer = setTimeout(() => {
        if (this.state !== 'pending' || this.swipeDisabled()) {
          return
        }

        this.state = 'hold'
        this.setGesturePressing(false)
        this.suppressNextClick = true
        this.longPressed.emit()
      }, LONG_PRESS_MS)
    }
  }

  /**
   * Locks the gesture direction on the first movement, then drags the row.
   * @param {number} x
   * @param {number} y
   */
  private pointerMove(x: number, y: number): void {
    if (this.state === 'pending') {
      const dx = x - this.startX
      const dy = y - this.startY

      if (Math.abs(dx) < LOCK_SLOP && Math.abs(dy) < LOCK_SLOP) {
        return
      }

      clearTimeout(this.holdTimer)

      const openingDirection = this.swipeDirection() === 'left' ? -1 : 1
      const tray = this.rowActions()
      const scrollsActions =
        this.gestureOnActions &&
        (dx * openingDirection > 0 || (!!tray && tray.scrollLeft > 0 && dx * openingDirection < 0))

      if (Math.abs(dy) > Math.abs(dx) || scrollsActions) {
        this.setGesturePressing(false)
        this.suppressNextClick = scrollsActions
        this.state = 'scroll'

        return
      }

      this.startDrag(dx)
    }

    if (this.state !== 'drag') {
      return
    }

    this.recordSample(x)

    const dx = x - this.startX

    this.position = this.clampPosition(this.basePosition + dx)

    if (!this.hasRevealedActions && Math.abs(this.position) < ACTION_REVEAL_THRESHOLD) {
      return
    }

    if (!this.hasRevealedActions) {
      this.revealActions()
    }

    if (Math.abs(this.position) > 3) {
      this.suppressNextClick = true
    }

    this.renderRow(this.position)
    this.renderTray(this.position)
    this.updateFullSwipe()
  }

  /**
   * Switches to dragging. Other open rows close, like a native list.
   * @param {number} dx
   */
  private startDrag(dx: number): void {
    this.state = 'drag'
    this.suppressNextClick = this.hasRevealedActions
    // The finger already moved by the slop; start from there so the row does not jump.
    this.startX += Math.sign(dx) * LOCK_SLOP
    this.rowSwipeService.inProgress = true
    this.rowSwipeService.closeRows(this)
  }

  /** Reveals the action tray after deliberate horizontal travel and starts its entry animation. */
  private revealActions(): void {
    this.hasRevealedActions = true
    this.setTrayVisible(true)
    this.renderer.setStyle(this.row.nativeElement, 'will-change', 'transform')
    this.showActions.emit(true)

    const rowActions = this.rowActions()

    if (rowActions) {
      this.renderer.removeClass(rowActions, 'swipe-row__actions--revealed')
      this.renderer.addClass(rowActions, 'swipe-row__actions--revealed')
    }
  }

  /**
   * Keeps the row inside its range, with rubber-band resistance past the action tray.
   * @param {number} raw
   * @returns {number}
   */
  private clampPosition(raw: number): number {
    const sign = this.swipeDirection() === 'left' ? -1 : 1
    const towardOpen = raw * sign

    if (towardOpen <= 0) {
      return 0
    }

    const limit = this.dragLimit()

    if (towardOpen <= limit) {
      return raw
    }

    if (this.swipeAction()) {
      // A full swipe follows the finger so the action can fill the row.
      return raw
    }

    const width = this.row.nativeElement.offsetWidth || limit * 2
    const over = towardOpen - limit
    const rubber = (1 - 1 / ((over * RUBBER_BAND) / width + 1)) * width

    return sign * (limit + rubber)
  }

  /** Arms or disarms the full-swipe action and gives a light haptic tick when it arms. */
  private updateFullSwipe(): void {
    const rowActions = this.rowActions()

    if (!this.swipeAction() || !rowActions) {
      return
    }

    const armed = Math.abs(this.position) > this.dragLimit() + this.swipeActionOffset()

    if (armed === this.fullSwipeArmed) {
      return
    }

    this.fullSwipeArmed = armed

    if (armed) {
      this.renderer.addClass(rowActions, 'row-action-active')
      this.hapticService.swipeThreshold()
    } else {
      this.renderer.removeClass(rowActions, 'row-action-active')
    }
  }

  /**
   * Ends the gesture. A tap changes nothing; a drag settles open or closed by projected rest point.
   * @param {Event} [event]
   */
  private release(event?: Event): void {
    clearTimeout(this.holdTimer)
    this.stopGestureListeners()
    this.setGesturePressing(false)

    if (this.suppressNextClick) {
      this.clickResetTimer = setTimeout(() => (this.suppressNextClick = false), 500)
    }

    const state = this.state

    this.state = 'idle'

    if (state === 'pending' || state === 'scroll') {
      if (state === 'pending' && this.gestureOnActions) {
        return
      }

      if (state === 'pending' && this.isOpen) {
        // A tap on an open row closes it and must not open the row's content.
        event?.stopPropagation()
        this.suppressNextClick = true
        this.closeRow()
      } else if (state === 'pending' && Math.abs(this.basePosition) > 0.3) {
        // Finish an interrupted close before allowing the row's click through.
        event?.stopPropagation()
        this.suppressNextClick = true
        this.closeRow()
      }

      return
    }

    if (state !== 'drag') {
      return
    }

    this.renderer.removeStyle(this.row.nativeElement, 'will-change')

    if (!this.hasRevealedActions) {
      this.rowSwipeService.inProgress = false
      this.samples = []
      this.position = 0

      return
    }

    if (this.fullSwipeArmed) {
      this.fullSwipeArmed = false

      const rowActions = this.rowActions()

      if (rowActions) {
        this.renderer.removeClass(rowActions, 'row-action-active')
      }

      this.onSwipeAction.emit()
      this.closeRow()
    } else if (this.shouldOpen()) {
      this.openRow()
    } else {
      this.closeRow()
    }

    if (this.isOpen) {
      event?.stopPropagation()
    }

    if (this.suppressNextClick) {
      this.clickResetTimer = setTimeout(() => (this.suppressNextClick = false), 500)
    }
  }

  /**
   * Keeps the list row pressed while a touch is deciding between tap and swipe.
   * @param {boolean} pressing
   */
  private setGesturePressing(pressing: boolean): void {
    if (pressing) {
      this.renderer.addClass(this.row.nativeElement, 'row-swipe-pressing')
    } else {
      this.renderer.removeClass(this.row.nativeElement, 'row-swipe-pressing')
    }
  }

  /**
   * Projects where the row would coast to and opens when that passes half the tray.
   * @returns {boolean}
   */
  private shouldOpen(): boolean {
    const sign = this.swipeDirection() === 'left' ? -1 : 1
    const projected = (this.position + this.releaseVelocity() * PROJECTION_FACTOR) * sign

    return projected >= this.dragLimit() / 2
  }

  /**
   * Release velocity in px/ms from the last samples.
   * @returns {number}
   */
  private releaseVelocity(): number {
    if (this.samples.length < 2) {
      return 0
    }

    const last = this.samples[this.samples.length - 1]
    const first = this.samples[0]
    const dt = last.t - first.t

    return dt > 0 ? (last.x - first.x) / dt : 0
  }

  private recordSample(x: number): void {
    const now = performance.now()

    this.samples.push({ x, t: now })
    this.samples = this.samples.filter(sample => now - sample.t <= VELOCITY_WINDOW)
  }

  private openPosition(): number {
    return this.swipeDirection() === 'left' ? -this.dragLimit() : this.dragLimit()
  }

  /**
   * Moves the row to a target with a critically damped spring that starts at the release velocity.
   * @param {number} target Row translation in px.
   * @param {number} velocity Start velocity in px/ms.
   * @param {() => void} [done] Runs when the row rests.
   */
  private settle(target: number, velocity: number, done?: () => void): void {
    this.stopSettle()

    const reducedMotion = this.document.defaultView?.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const canAnimate = typeof requestAnimationFrame === 'function' && !reducedMotion

    if (!canAnimate || this.position === target) {
      this.position = target
      this.renderRow(target)
      this.renderTray(target)
      // Keep a short delay so callers see the same timing with and without animation.
      this.settleTimer = setTimeout(() => done?.(), canAnimate ? 0 : 200)

      return
    }

    let x = this.position
    let v = velocity
    let last = performance.now()
    const start = last
    const step = (now: number): void => {
      const dt = Math.min(now - last, SPRING_STEP_MS * 2) / 1000

      last = now

      const acceleration = -SPRING_STIFFNESS * (x - target) - SPRING_DAMPING * (v * 1000)

      v += (acceleration * dt) / 1000
      x += v * 1000 * dt

      const atRest = Math.abs(x - target) < 0.3 && Math.abs(v) < 0.02

      if (atRest || now - start > SPRING_MAX_MS) {
        x = target
      }

      this.position = x
      this.renderRow(x)
      this.renderTray(x)

      if (x === target) {
        this.settleFrame = undefined
        done?.()

        return
      }

      this.settleFrame = requestAnimationFrame(step)
    }

    this.ngZone.runOutsideAngular(() => {
      this.settleFrame = requestAnimationFrame(step)
    })
  }

  private stopSettle(): void {
    if (this.settleFrame !== undefined) {
      cancelAnimationFrame(this.settleFrame)
      this.settleFrame = undefined
    }

    clearTimeout(this.settleTimer)
    this.settleTimer = undefined
  }

  /**
   * Moves the content and exposes swipe feedback until it returns to rest.
   * @param {number} position Horizontal offset in pixels.
   */
  private renderRow(position: number): void {
    this.renderer.setStyle(this.row.nativeElement, 'transform', position ? `translateX(${position}px)` : 'none')

    if (position !== 0) {
      this.renderer.addClass(this.row.nativeElement, 'row-swipe-active')
    } else {
      this.renderer.removeClass(this.row.nativeElement, 'row-swipe-active')
      this.renderer.removeClass(this.row.nativeElement, 'row-swipe-closing')
    }
  }

  /**
   * Slides the tray in step with the row, or just shows it when it does not slide.
   * @param {number} position
   */
  private renderTray(position: number): void {
    const rowActions = this.rowActions()

    if (!rowActions) {
      return
    }

    if (!this.slideActions()) {
      this.renderer.setStyle(rowActions, 'display', Math.abs(position) > 10 || this.isOpen ? 'inline-flex' : 'none')

      return
    }

    const limit = this.dragLimit()
    const remaining = Math.max(0, limit - Math.min(Math.abs(position), limit))
    const direction = this.swipeDirection() === 'left' ? 1 : -1

    this.renderer.setStyle(rowActions, 'display', 'inline-flex')
    this.renderer.setStyle(rowActions, 'transform', `translateX(${remaining * direction}px)`)
  }

  private setTrayVisible(visible: boolean): void {
    const rowActions = this.rowActions()

    if (!rowActions) {
      return
    }

    if (this.slideActions()) {
      this.renderer.setStyle(rowActions, 'visibility', visible ? 'visible' : 'hidden')
    } else if (!visible) {
      this.renderer.setStyle(rowActions, 'display', 'none')
    }
  }

  /**
   * Closes the open row when the user touches anything outside the row and its actions.
   * Like iOS, that touch only dismisses the actions; its click does not reach the target.
   */
  private listenForOutsideTap(): void {
    this.removeOutsideListener?.()

    const onOutside = (event: Event): void => {
      const target = event.target as Node | null

      if (!target || this.row.nativeElement.contains(target) || this.rowActions()?.contains(target)) {
        return
      }

      this.swallowNextClick()
      this.ngZone.run(() => this.closeRow())
    }

    this.ngZone.runOutsideAngular(() => {
      this.document.addEventListener('touchstart', onOutside, { capture: true, passive: true })
      this.document.addEventListener('mousedown', onOutside, { capture: true })
    })
    this.removeOutsideListener = () => {
      this.document.removeEventListener('touchstart', onOutside, { capture: true })
      this.document.removeEventListener('mousedown', onOutside, { capture: true })
    }
  }

  /** Blocks the click that follows the current touch anywhere in the document. */
  private swallowNextClick(): void {
    const onClick = (event: Event): void => {
      event.preventDefault()
      event.stopImmediatePropagation()
      stop()
    }
    const stop = (): void => {
      clearTimeout(timer)
      this.document.removeEventListener('click', onClick, { capture: true })
    }

    this.document.addEventListener('click', onClick, { capture: true })

    const timer = setTimeout(stop, 500)
  }

  private stopGestureListeners(): void {
    this.removeListeners.forEach(remove => remove())
    this.removeListeners = []
  }
}
